import {
  Box,
  Button,
  Center,
  HStack,
  VStack,
  useColorModeValue,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { Api } from 'api';
import { FormControlFormInput, updateLocalStorageAuthInfo, GenericMessages, usePageTitle } from 'core';
import * as yup from 'yup';
import { setAuthUserInfo } from 'features';
import { useFormik } from 'formik';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { AuthUserInfo, LoginInfo } from 'types';
import { useState, useEffect } from 'react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';

export const LoginPage = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Set page title
  usePageTitle('Giriş');
  const forgotPasswordMutation = useMutation<any, any, { email: string }>(
    async ({ email }) => {
      const response = await Api.Auth.forgotPassword(email);
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          toast({
            title: 'Şifre Sıfırla',
            description: data.message || 'E-posta adresinize şifre sıfırlama linki gönderildi.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          onClose();
        } else {
          toast({
            title: 'Hata',
            description: data.message || 'Şifre sıfırlama linki gönderilemedi.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      },
      onError: () => {
        toast({
          title: 'Hata',
          description: 'Şifre yenileme linki gönderilemedi.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const resetPasswordMutation = useMutation<any, any, { token: string; password: string; confirmPassword: string }>(
    async ({ token, password, confirmPassword }) => {
      const response = await Api.Auth.resetPassword(token, password, confirmPassword);
      return response.data;
    },
    {
      onSuccess: (data) => {
        if (data.success) {
          toast({
            title: 'Şifre Sıfırlama',
            description: data.message || 'Yeni şifrenizle giriş yapabilirsiniz.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          setShowResetPassword(false);
          setResetToken(null);
        } else {
          toast({
            title: 'Hata',
            description: data.message || 'Şifre sıfırlanamadı. Lütfen tekrar deneyin.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      },
      onError: () => {
        toast({
          title: 'Hata',
          description: 'Şifre sıfırlanamadı. Lütfen tekrar deneyin.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const mutation = useMutation<LoginInfo, any, { email: string; password: string }>(
    async ({ email, password }) => {
      const response = await Api.Auth.login(email, password);
      return response.data;
    },
    {
      onSuccess: (loginInfo) => {
        const authUserInfo: AuthUserInfo = { ...loginInfo.user, token: loginInfo.token };
        Api.token = loginInfo.token;
        dispatch(setAuthUserInfo(authUserInfo));
        updateLocalStorageAuthInfo(authUserInfo);
        window.location.href = '/';
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || 'Giriş başarısız. Lütfen tekrar deneyin.';
        toast({
          title: 'Hata',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const { values, handleChange, handleSubmit, errors } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    onSubmit: (formValues) => {
      mutation.mutateAsync(formValues);
    },
  });

  const forgotPasswordValidationSchema = yup.object({
    email: yup.string().required(GenericMessages.CannotBeEmpty).email(GenericMessages.InvalidEmail),
  });

  const forgotPasswordFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordValidationSchema,
    onSubmit: (formValues) => {
      forgotPasswordMutation.mutateAsync(formValues);
    },
  });

  const resetPasswordValidationSchema = yup.object({
    password: yup.string().required(GenericMessages.CannotBeEmpty).min(6, GenericMessages.CantBeLessThan(6)),
    confirmPassword: yup
      .string()
      .required(GenericMessages.CannotBeEmpty)
      .oneOf([yup.ref('password')], GenericMessages.PasswordsDoNotMatch),
  });

  const resetPasswordFormik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: resetPasswordValidationSchema,
    onSubmit: (formValues) => {
      if (resetToken) {
        resetPasswordMutation.mutateAsync({
          token: resetToken,
          password: formValues.password,
          confirmPassword: formValues.confirmPassword,
        });
      }
    },
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setShowResetPassword(true);
    }
  }, []);
  return (
    <Center height="100vh">
      <Box boxShadow="lg" p="8" rounded="md" bg={useColorModeValue('white', 'gray.900')}>
        <form noValidate onSubmit={handleSubmit}>
          <VStack spacing={10} align="stretch">
            <FormControlFormInput
              label="Kullanıcı Adı"
              placeholder="Kullanıcı Adı"
              onChange={handleChange}
              name="email"
              value={values.email}
              helperText={errors.email}
              error={errors.email != null}
            />
            <Box position="relative">
              <FormControlFormInput
                label="Parola"
                placeholder="Parola"
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                name="password"
                value={values.password}
                helperText={errors.password}
                error={errors.password != null}
              />

              <IconButton
                h="1.75rem"
                size="sm"
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', top: '38px', right: '0', background: 'transparent', zIndex: 99 }}
              />
            </Box>
            <VStack spacing={4}>
              <HStack justify="flex-end" width="100%">
                <Button colorScheme="blue" isLoading={mutation.isLoading} type="submit">
                  Giriş Yap
                </Button>
              </HStack>
              <HStack justify="flex-end" width="100%">
                <Button variant="link" color={useColorModeValue('black', 'white')} onClick={onOpen}>
                  Şifremi Unuttum
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </form>
      </Box>

      {/* Forgot Password Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Şifremi Unuttum</ModalHeader>
          <ModalBody>
            <form onSubmit={forgotPasswordFormik.handleSubmit}>
              <FormControlFormInput
                label="E-posta Adresi"
                placeholder="E-posta adresinizi girin"
                onChange={forgotPasswordFormik.handleChange}
                name="email"
                value={forgotPasswordFormik.values.email}
                helperText={forgotPasswordFormik.errors.email}
                error={forgotPasswordFormik.errors.email != null}
              />
            </form>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              isLoading={forgotPasswordMutation.isLoading}
              onClick={() => forgotPasswordFormik.handleSubmit()}
            >
              Gönder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={showResetPassword} onClose={() => setShowResetPassword(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Yeni Şifre Belirle</ModalHeader>
          <ModalBody>
            <form onSubmit={resetPasswordFormik.handleSubmit}>
              <VStack spacing={4}>
                <FormControlFormInput
                  label="Yeni Şifre"
                  placeholder="Yeni şifrenizi girin"
                  type="password"
                  onChange={resetPasswordFormik.handleChange}
                  name="password"
                  value={resetPasswordFormik.values.password}
                  helperText={resetPasswordFormik.errors.password}
                  error={resetPasswordFormik.errors.password != null}
                />
                <FormControlFormInput
                  label="Şifre Tekrar"
                  placeholder="Şifrenizi tekrar girin"
                  type="password"
                  onChange={resetPasswordFormik.handleChange}
                  name="confirmPassword"
                  value={resetPasswordFormik.values.confirmPassword}
                  helperText={resetPasswordFormik.errors.confirmPassword}
                  error={resetPasswordFormik.errors.confirmPassword != null}
                />
              </VStack>
            </form>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setShowResetPassword(false)}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              isLoading={resetPasswordMutation.isLoading}
              onClick={() => resetPasswordFormik.handleSubmit()}
            >
              Şifreyi Sıfırla
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Center>
  );
};
